/**
 * AssistantText — renders the model's prose with the minimal markdown
 * subset it actually emits, AND emits inline citation cards after
 * each paragraph that referenced one.
 *
 * Sprint AI-MODULE v2.1 follow-up. Without this:
 *   - ``[text](url)`` came through as literal text
 *   - ``[N]`` citation refs were just text in the message, and the
 *     real link/card lived buried in a collapsed "N sources" list
 *
 * Now:
 *   - Markdown subset is parsed: ``[text](url)`` links,
 *     ``**bold**``, ``- item`` / ``1.`` lists, ``## heading``
 *   - For every ``[N]`` citation reference where N maps to a
 *     card-kind citation (cw_file, dm_document), an
 *     ``<InlineCitationCard>`` is rendered immediately after the
 *     paragraph. The card carries metadata + a live preview iframe
 *     (Drive / M365 where supported) + the click-through link.
 *   - Each citation is rendered at most once per message — if the
 *     model references the same `[1]` from three paragraphs, the
 *     card appears under the first one only.
 */
import { Fragment, type FC, type ReactNode } from 'react';
import type { CitationRef } from '../api/aiApi';
import InlineCitationCard from './InlineCitationCard';
import VmInlineCard from './VmInlineCard';

// File-shaped citations rendered via InlineCitationCard (live preview).
const FILE_CARD_KINDS = new Set(['cw_file', 'dm_document']);
// VM-shaped citations rendered via VmInlineCard (action-aware CTAs).
const VM_CARD_KINDS = new Set(['vm_vendor', 'vm_pr', 'vm_contract', 'vm_po']);
const CARD_KINDS = new Set<string>([...FILE_CARD_KINDS, ...VM_CARD_KINDS]);

interface Props {
  text: string;
  citations?: CitationRef[];
}

const AssistantText: FC<Props> = ({ text, citations = [] }) => {
  if (!text) return null;

  const byId = new Map<number, CitationRef>();
  for (const c of citations) byId.set(c.id, c);
  const renderedCards = new Set<number>();

  const blocks = splitBlocks(text);
  return (
    <div className="ai-chat__assistant-text">
      {blocks.map((b, i) => (
        <Fragment key={i}>
          {renderBlock(b, i)}
          {/* After each block, emit any card-kind citations newly
              referenced inside it. */}
          {emitCardsForBlock(b, byId, renderedCards)}
        </Fragment>
      ))}
    </div>
  );
};

export default AssistantText;


function emitCardsForBlock(
  b: Block,
  byId: Map<number, CitationRef>,
  rendered: Set<number>,
): ReactNode {
  const text = textOfBlock(b);
  const refIds = extractCitationIds(text);
  const cards: CitationRef[] = [];
  for (const id of refIds) {
    if (rendered.has(id)) continue;
    const c = byId.get(id);
    if (!c) continue;
    if (!CARD_KINDS.has(c.kind)) continue;
    cards.push(c);
    rendered.add(id);
  }
  if (!cards.length) return null;
  return (
    <div className="ai-chat__inline-cards">
      {cards.map((c) =>
        VM_CARD_KINDS.has(c.kind)
          ? <VmInlineCard key={c.id} citation={c} />
          : <InlineCitationCard key={c.id} citation={c} />,
      )}
    </div>
  );
}

function textOfBlock(b: Block): string {
  switch (b.kind) {
    case 'paragraph': return b.lines.join('\n');
    case 'ul':
    case 'ol': return b.items.join('\n');
    case 'heading': return b.text;
  }
}

// Find [N] citation references in a string. Avoids matching markdown
// links — only counts `[N]` that is NOT followed by `(`.
const CITE_RE = /\[(\d+)\](?!\()/g;
function extractCitationIds(s: string): number[] {
  const out: number[] = [];
  let m: RegExpExecArray | null;
  CITE_RE.lastIndex = 0;
  while ((m = CITE_RE.exec(s)) !== null) {
    out.push(parseInt(m[1]!, 10));
  }
  return out;
}


// ── Block-level parsing ─────────────────────────────────────────────

type Block =
  | { kind: 'paragraph'; lines: string[] }
  | { kind: 'ul' | 'ol'; items: string[] }
  | { kind: 'heading'; level: number; text: string };

function splitBlocks(src: string): Block[] {
  const lines = src.replace(/\r\n/g, '\n').split('\n');
  const out: Block[] = [];
  let para: string[] = [];
  let list: { kind: 'ul' | 'ol'; items: string[] } | null = null;

  const flushPara = () => {
    if (para.length) {
      out.push({ kind: 'paragraph', lines: para });
      para = [];
    }
  };
  const flushList = () => {
    if (list) {
      out.push(list);
      list = null;
    }
  };

  for (const raw of lines) {
    const line = raw;
    if (/^\s*$/.test(line)) {
      flushPara();
      flushList();
      continue;
    }
    const heading = line.match(/^(#{1,4})\s+(.*)$/);
    if (heading) {
      flushPara();
      flushList();
      out.push({ kind: 'heading', level: heading[1]!.length, text: heading[2]! });
      continue;
    }
    const ul = line.match(/^\s*[-*+]\s+(.*)$/);
    const ol = line.match(/^\s*(\d+)\.\s+(.*)$/);
    if (ul) {
      flushPara();
      if (!list || list.kind !== 'ul') {
        flushList();
        list = { kind: 'ul', items: [] };
      }
      list.items.push(ul[1]!);
      continue;
    }
    if (ol) {
      flushPara();
      if (!list || list.kind !== 'ol') {
        flushList();
        list = { kind: 'ol', items: [] };
      }
      list.items.push(ol[2]!);
      continue;
    }
    flushList();
    para.push(line);
  }
  flushPara();
  flushList();
  return out;
}

function renderBlock(b: Block, key: number): ReactNode {
  switch (b.kind) {
    case 'heading':
      return (
        <h4 key={key} className="ai-chat__md-heading">
          {renderInline(b.text)}
        </h4>
      );
    case 'ul':
      return (
        <ul key={key} className="ai-chat__md-ul">
          {b.items.map((it, i) => <li key={i}>{renderInline(it)}</li>)}
        </ul>
      );
    case 'ol':
      return (
        <ol key={key} className="ai-chat__md-ol">
          {b.items.map((it, i) => <li key={i}>{renderInline(it)}</li>)}
        </ol>
      );
    case 'paragraph':
      return (
        <p key={key} className="ai-chat__md-p">
          {b.lines.map((line, i) => (
            <Fragment key={i}>
              {i > 0 && <br />}
              {renderInline(line)}
            </Fragment>
          ))}
        </p>
      );
  }
}


// ── Inline parsing ──────────────────────────────────────────────────
// Handle [text](url) links + **bold** in a single pass. Order
// matters: link parse first (link text might contain bold), then
// bold inside the remaining text spans.

const LINK_RE = /\[([^\]\n]+)\]\((https?:\/\/[^\s)]+|\/[^\s)]+)\)/g;
const BOLD_RE = /\*\*([^*\n]+)\*\*/g;

function renderInline(line: string): ReactNode {
  const parts: ReactNode[] = [];
  let last = 0;
  let match: RegExpExecArray | null;
  LINK_RE.lastIndex = 0;
  while ((match = LINK_RE.exec(line)) !== null) {
    if (match.index > last) {
      parts.push(...renderBoldSpans(line.slice(last, match.index), `t${parts.length}`));
    }
    const label = match[1]!;
    const href = match[2]!;
    const isExternal = /^https?:\/\//i.test(href);
    parts.push(
      <a
        key={`a${parts.length}`}
        href={href}
        target={isExternal ? '_blank' : undefined}
        rel={isExternal ? 'noopener noreferrer' : undefined}
        className="ai-chat__md-link"
      >
        {renderBoldSpans(label, `l${parts.length}`)}
      </a>,
    );
    last = match.index + match[0].length;
  }
  if (last < line.length) {
    parts.push(...renderBoldSpans(line.slice(last), `t${parts.length}`));
  }
  return parts;
}

function renderBoldSpans(text: string, keyPrefix: string): ReactNode[] {
  const parts: ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  BOLD_RE.lastIndex = 0;
  while ((m = BOLD_RE.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push(<strong key={`${keyPrefix}b${m.index}`}>{m[1]!}</strong>);
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length > 0 ? parts : [text];
}
